// useStakeRecommendation — pre-bet stake recommendation hook.
//
// Wraps the rollover_full_recommendation(p_chain_id, p_odds, p_model_prob?, p_confidence?)
// RPC. The RPC composes:
//   • Phase Withdrawal v2.2 (phase_stake_pct from rollover_recommend_stake_pct)
//   • Kelly floor (when chain.kelly_floor_enabled and model_prob is provided)
//   • Confidence multiplier (1.10 / 1.00 / 0.85 by tier)
//   • Hard caps at 20% (min) and 80% (max) of projected_win
//   • GH₵50 rounding
//
// This is distinct from useCompoundingDecision (which serves the POST-WIN
// "what do I keep, what do I roll" Vault surface). Both hooks coexist.
//
// Inputs:
//   chainId — required; returns null on null/empty
//   odds    — required positive number; the upcoming bet's combined odds
//   modelProb / confidence — optional refinements
//
// Outputs the full RPC payload plus a normalized error string.

import { useMemo } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { useRpcQuery } from "../data/useRpcQuery.ts";

export interface StakeRecommendation {
  chain_id: string;
  this_week: number;
  phase: number;
  phase_stake_pct: number;
  projected_win: number;
  kelly_fraction: number;
  kelly_floor_value: number;
  confidence_multiplier: number;
  recommended_stake: number;
  recommended_bankroll: number;
  odds: number;
  model_prob: number | null;
  confidence: number | null;
  rationale:
    | "kelly_floor_active"
    | "confidence_pulled_back"
    | "confidence_amplified"
    | "phase_withdrawal_default"
    | string;
}

export interface StakeRecommendationState {
  recommendation: StakeRecommendation | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function asN(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") return Number(v);
  return Number.NaN;
}

export function useStakeRecommendation(
  chainId: string | null,
  odds: number | null,
  modelProb?: number | null,
  confidence?: number | null,
): StakeRecommendationState {
  const { user } = useAuthSession();

  const enabled =
    Boolean(user) && Boolean(chainId) && Number.isFinite(odds) && (odds ?? 0) > 1;

  const args = useMemo<Record<string, unknown>>(() => {
    const a: {
      p_chain_id: string;
      p_odds: number;
      p_model_prob?: number;
      p_confidence?: number;
    } = { p_chain_id: chainId ?? "", p_odds: odds ?? 0 };
    if (typeof modelProb === "number" && Number.isFinite(modelProb)) {
      a.p_model_prob = modelProb;
    }
    if (typeof confidence === "number" && Number.isFinite(confidence)) {
      a.p_confidence = confidence;
    }
    return a;
  }, [chainId, odds, modelProb, confidence]);

  // Generated db/types.ts hasn't been regenerated since the
  // rollover_full_recommendation RPC landed in Phase 2.
  const q = useRpcQuery<Record<string, unknown> | null>(
    "rollover_full_recommendation",
    { args, enabled },
  );

  // The RPC can return a soft error envelope (e.g. 'chain_not_active',
  // 'active_stake_is_zero') as { error: string }; surface it as an error.
  const envelopeError = useMemo<string | null>(() => {
    const data = q.data;
    if (data && typeof data.error === "string") return data.error;
    return null;
  }, [q.data]);

  const recommendation = useMemo<StakeRecommendation | null>(() => {
    const data = q.data;
    if (!data) return null;
    if (typeof data.error === "string") return null;
    return {
      chain_id: String(data.chain_id ?? chainId),
      this_week: Math.round(asN(data.this_week)),
      phase: Math.round(asN(data.phase)),
      phase_stake_pct: asN(data.phase_stake_pct),
      projected_win: asN(data.projected_win),
      kelly_fraction: asN(data.kelly_fraction),
      kelly_floor_value: asN(data.kelly_floor_value),
      confidence_multiplier: asN(data.confidence_multiplier),
      recommended_stake: asN(data.recommended_stake),
      recommended_bankroll: asN(data.recommended_bankroll),
      odds: asN(data.odds),
      model_prob:
        typeof data.model_prob === "number" || typeof data.model_prob === "string"
          ? asN(data.model_prob)
          : null,
      confidence:
        typeof data.confidence === "number" || typeof data.confidence === "string"
          ? asN(data.confidence)
          : null,
      rationale: typeof data.rationale === "string" ? data.rationale : "phase_withdrawal_default",
    };
  }, [q.data, chainId]);

  return useMemo<StakeRecommendationState>(
    () => ({
      recommendation,
      loading: q.isLoading,
      error: q.error ? q.error.message : envelopeError,
      refetch: async () => {
        await q.refetch();
      },
    }),
    [recommendation, q.isLoading, q.error, envelopeError, q.refetch],
  );
}
